import { Link } from 'react-router-dom';
import { PageHero } from '../../components/PageHero';
import SEO from '../../components/SEO';

const timeline = [
  { year: '2023', items: [
    { month: 'March 2023',     text: 'Conceptualized Manna as a modular ERP platform, starting with the publishing ecosystem.' },
    { month: 'June 2023',      text: 'Initial architecture and core platform design completed, focusing on scalability and multi-tenant SaaS.' },
    { month: 'September 2023', text: 'Development began on Manna Manuscript Management as the first flagship module.' },
  ]},
  { year: '2024', items: [
    { month: 'January 2024', text: 'Core workflows for submission, peer review, and editorial management implemented.' },
    { month: 'April 2024',   text: 'AI capabilities introduced, including plagiarism checks, content analysis, and workflow automation.' },
    { month: 'August 2024',  text: 'Expanded feature set based on early feedback from publishers and editors.' },
  ]},
  { year: '2025', items: [
    { month: 'January 2025', text: 'Stabilized the platform with performance, security, and usability improvements.' },
    { month: 'March 2025',   text: 'Pilot launch of Manna Manuscript Management, marking the first official product release.' },
  ]},
  { year: 'Today', items: [
    { month: 'Today', text: 'Actively onboarding early users and partners while expanding the Manna ERP ecosystem.' },
  ]},
];

const values = [
  { title: 'Our Vision',   desc: 'To empower small and medium sized enterprises worldwide with intelligent, AI-enabled automation solutions that simplify operations, improve decision making, and enable sustainable growth.' },
  { title: 'Our Mission',  desc: 'We envision a future where advanced technology is accessible, practical, and built around real business needs without complexity — a company with a global vision built to serve businesses around the world.' },
  { title: 'Our Values',   desc: 'Software isn\'t just our paycheck. Our teams spend years mastering their craft to deliver exceptional products that customers love. Cutting edge technology meets timeless values.' },
  { title: 'Our Approach', desc: 'From the way we design our products to how we grow our team, we focus on building technology that serves diverse businesses with clarity and purpose. Every decision is guided by long-term impact.' },
];

export default function About() {
  return (
    <div>
      <SEO
        title="About MannaPlag: Academic Integrity & AI Detection Mission"
        description="Discover how Manna Analytics is modernizing academic publishing and manuscript integrity with AI-driven plagiarism detection and research verification tools."
        canonicalPath="/about"
        keywords="about MannaPlag, Manna Analytics Private Limited, academic publishing software, research integrity company, ethical AI detection"
      />
      <PageHero
        eyebrow="About us"
        title="For over a decade, we've been redefining how publishers and enterprises operate"
        subtitle="At Manna, we build AI-driven ERP systems and manuscript management platforms designed specifically for the publishing industry. Our mission is to simplify workflows, empower teams, and enable scalable digital transformation."
      />

      {/* Stats */}
      <section className="bg-navy border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[['10+','Years of experience'],['AI','Driven platform'],['Global','Perspective'],['SME','Focused solutions']].map(([val,label])=>(
            <div key={label}>
              <p className="text-[22px] font-medium text-blue-300 mb-1">{val}</p>
              <p className="text-[11px] text-white/50">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What we do */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-[22px] font-medium text-gray-900 mb-4">Cutting edge technology meets timeless values</h2>
              <p className="text-[13px] text-gray-500 leading-relaxed mb-4">
                We believe that software is the ultimate product of the hands and the mind. It's our passion. Our teams spend years mastering their craft to deliver exceptional products that customers love.
              </p>
              <p className="text-[13px] text-gray-500 leading-relaxed">
                Manna Analytics Private Limited is headquartered in Nagercoil, Tamil Nadu, India — a company with a global vision built to serve businesses around the world.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {values.map(v => (
                <div key={v.title} className="bg-white border border-gray-100 rounded-xl p-5">
                  <p className="text-[12px] font-medium text-blue-600 mb-2">{v.title}</p>
                  <p className="text-[11px] text-gray-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-[22px] font-medium text-gray-900 mb-2 text-center">Our journey</h2>
          <p className="text-[13px] text-gray-500 text-center mb-12">Manna wasn't built in a day</p>
          <div className="max-w-2xl mx-auto">
            {timeline.map(group => (
              <div key={group.year} className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center flex-shrink-0">
                    <span className="text-[11px] font-medium text-white">{group.year}</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-100"/>
                </div>
                <div className="ml-5 flex flex-col gap-4">
                  {group.items.map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"/>
                        {i < group.items.length - 1 && <div className="w-px flex-1 bg-gray-100 mt-1"/>}
                      </div>
                      <div className="pb-4">
                        <p className="text-[11px] font-medium text-gray-700 mb-1">{item.month}</p>
                        <p className="text-[12px] text-gray-500 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Address */}
      <section className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <p className="text-[13px] font-medium text-gray-900 mb-3">Registered office</p>
              <p className="text-[12px] text-gray-500 leading-relaxed">
                Manna Analytics Private Limited<br/>
                3/343, Town Railway Nagar<br/>
                Nagercoil 629001<br/>
                Tamil Nadu, India
              </p>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <p className="text-[13px] font-medium text-gray-900 mb-3">Get in touch</p>
              <p className="text-[12px] text-gray-500 leading-relaxed mb-3">
                For general enquiries, support, or partnership opportunities, visit our contact page.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-1 text-[12px] text-blue-600 hover:underline">
                Contact us
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
